/**
 * The rules-version workflow as the 版本 page presents it: which actions a version offers to the
 * current admin, and what a failed action means.
 *
 *   draft --submit--> in_review --publish--> published --(next publish)--> archived
 *                         \--reject--> draft
 *
 * The service enforces all of this; the page only hides actions that would be refused.
 */
import type { RuleVersionSummary } from '../client/CpaClient';
import { isAccessDenied } from './programs';

export type VersionAction = 'submit' | 'discard' | 'publish' | 'reject' | 'implemented';

/** Actions on `v` for `alias`. The author of a version cannot publish it. */
export function actionsFor(v: RuleVersionSummary, alias: string): VersionAction[] {
  switch (v.status) {
    case 'draft':
      return ['submit', 'discard'];
    case 'in_review':
      return v.author === alias ? ['reject'] : ['publish', 'reject'];
    case 'published':
      return v.engineSync?.status === 'implemented' ? [] : ['implemented'];
    default:
      return [];
  }
}

/** The open version (draft or in review), if any: at most one exists at a time. */
export function openVersion(versions: RuleVersionSummary[]): RuleVersionSummary | undefined {
  return versions.find((v) => v.status === 'draft' || v.status === 'in_review');
}

/** What a failed workflow call means for the person. Returns an i18n key and the server message. */
export function workflowErrorOf(e: unknown): { key: string; detail?: string } {
  const err = e as { name?: string; message?: string } | null;
  // Denials are recognised the same way as elsewhere in CPA (exception name or HTTP 403).
  if (isAccessDenied(e)) {
    return { key: /author/i.test(err?.message ?? '') ? 'versionsOwnPublish' : 'programsDenied' };
  }
  switch (err?.name) {
    case 'ConflictException':
      return { key: 'versionsConflict', detail: err.message };
    case 'InvalidInputException':
      return { key: 'saveInvalid', detail: err.message };
    default:
      return { key: 'saveFailed', detail: err?.message };
  }
}
